// ============================================
// grokRegistry.js - open-ended Grok app actions (grok-generic-20261016).
//
// Register an action here and Grok can use it with NO server change:
//   registerGrokAction('my_action', {
//     description: 'What it does, when to use it (one or two lines).',
//     params: { type: 'object', properties: { x: { type: 'number' } }, required: ['x'] },
//     handler: async (args) => ({ ok: true }),   // return { ok, error?, ...data }
//   })
// The list (name, description, params) is sent with every @grok question
// (listGrokActions). The server exposes ONE tool, app_action {action, args},
// validates the name against that list, and forwards the call back as
// { op: 'app_action', action, args } in grok_actions -> runGrokAction().
// Handlers must validate their own args (treat them as untrusted input).
// ============================================

const NAME_RE = /^[a-z][a-z0-9_]{1,39}$/
const MAX_ACTIONS = 24          // keep in sync with server GROK_APP_MAX
const MAX_DESC = 300
const MAX_PARAMS_JSON = 1500

const registry = new Map()

export function registerGrokAction(name, { description = '', params, handler, hidden = false } = {}) {
  if (!NAME_RE.test(String(name))) throw new Error(`grok action name invalid: ${name}`)
  if (typeof handler !== 'function') throw new Error(`grok action ${name} needs a handler`)
  registry.set(name, {
    name,
    description: String(description).slice(0, MAX_DESC),
    params: params && typeof params === 'object' ? params : { type: 'object', properties: {} },
    handler,
    hidden: !!hidden,       // hidden = callable internally, not advertised to Grok
  })
  return () => { if (registry.get(name)?.handler === handler) registry.delete(name) }
}

export function unregisterGrokAction(name) { registry.delete(name) }

/** Advertised registry for the chat request (bounded). */
export function listGrokActions() {
  const out = []
  for (const a of registry.values()) {
    if (a.hidden) continue
    let params = a.params
    try { if (JSON.stringify(params).length > MAX_PARAMS_JSON) params = { type: 'object' } } catch { params = { type: 'object' } }
    out.push({ name: a.name, description: a.description, params })
    if (out.length >= MAX_ACTIONS) break
  }
  return out
}

export async function runGrokAction(name, args) {
  const a = registry.get(String(name))
  if (!a) return { ok: false, error: `unknown app action ${name}` }
  const safeArgs = args && typeof args === 'object' && !Array.isArray(args) ? args : {}
  try {
    const r = await a.handler(safeArgs)
    return r && typeof r === 'object' ? r : { ok: !!r }
  } catch (e) {
    return { ok: false, error: String(e?.message || e).slice(0, 200) }
  }
}
